<template>
  <div id="wrapper">
    <!--
      On a phone the sidebar is a slide-in menu (app.scss, the 768px block) and
      this is what lies behind it while it is open: tap anywhere else and it
      closes. Rendered only then, so a desktop never has it.
    -->
    <div
      v-if="narrow && !menuCollapsed"
      class="lw-p-shell__backdrop"
      data-test="menu-backdrop"
      @click="menuCollapsed = true"
    />
    <!--
      The rail's own collapse control emits here, because `menuCollapsed` lives
      in this component and drives both the sidebar's class and the header's
      narrow-screen button. Two writers, one piece of state, still in one place.
    -->
    <sidebar
      id="sidebar"
      :collapsed="menuCollapsed"
      :class="{ collapsed: menuCollapsed, expanded: !menuCollapsed }"
      app-title="Ladeway"
      :user="user"
      :narrow="narrow"
      @toggle-rail="menuCollapsed = !menuCollapsed"
    />

    <div id="main">
      <div id="header">
        <!--
          menuCollapsed is passed down so the button's aria-expanded can stay
          honest about the sidebar's actual state (audit_web.md 3.4: the
          toggle wraps an unlabelled icon with no aria-expanded at all). The
          @menuButton emit contract is unchanged, so this remains the only
          place the state is written.
        -->
        <header-component
          :menu-collapsed="menuCollapsed"
          :narrow="narrow"
          @menu-button="menuCollapsed = !menuCollapsed"
        />
      </div>

      <!--
        A sibling of #header/#content, not an overlay: #main is a column
        flexbox (ui/src/sass/app.scss L139-153) and #content is
        `flex: 1; overflow-y: auto`, so inserting the notice as a sibling
        shrinks the scrollable area to make room for it instead of floating
        on top and covering content underneath. That reasoning predates this
        edit and still applies; only the element inside it changed.

        audit_web.md 3.8 lists this as pattern G of nine unrelated notice
        patterns, and "none of A, B, C or G carries role="alert",
        role="status" or aria-live, so no notice in the SPA is announced".
        refresh_plan.md 3.1.7 collapses the nine to three (inline field
        error, page notice, toast); this is the page notice, and LwNotice IS
        the page notice, so it lives in the shell rather than as its own
        bespoke scoped banner. 'suspended' means operations are actually
        paused, which is LwNotice's danger tone (role="alert", interrupts);
        every other billing state (a payment reminder, an overdue warning) is
        'warning' (role="status"). LwNotice already renders a severity WORD
        for both tones even when no title is passed, so colour is never the
        only signal here — see displayTitle in LwNotice.vue — which is why
        this call passes no title of its own.

        "View subscription" goes in the actions slot rather than the default
        slot: it is a call to action to go somewhere else, not part of the
        sentence LwNotice renders as the message, and the actions slot is
        already laid out as a flex row for exactly this.
      -->
      <LwNotice
        v-if="showBanner"
        class="lw-p-billing-banner"
        :tone="billingStatus === 'suspended' ? 'danger' : 'warning'"
      >
        {{ bannerText }}
        <template #actions>
          <router-link class="lw-p-banner-link" :to="{ name: 'Subscription' }">
            View subscription
          </router-link>
        </template>
      </LwNotice>

      <!--
        A real <main>: audit_web.md's landmark findings note the SPA has
        essentially none. #main stays a plain div because it is the LAYOUT
        column (sidebar + header + notice + content), not the page's main
        content region, and <main> must not be duplicated on a page.
      -->
      <main id="content">
        <router-view />
      </main>
    </div>

    <!--
      The one toast region for the whole app (refresh_plan.md 3.1.7). It is
      mounted here, always, rather than by whichever page happens to need it:
      a live region that is created at the moment a message arrives is often
      not announced at all, so the region has to already exist and be empty.
      It renders no visible box until the queue has something in it, and it
      does not intercept pointer events while it is empty.

      Its first producer is this file's own network-failure interceptor
      below (surface 4, the shell): a request that never reached the server
      dispatches a pinned danger toast here rather than throwing the
      dispatcher out of the app. The rest of the nine notice patterns it will
      eventually replace live on surfaces that migrate later in phase 2, and
      moving them here would be a surface migration, which this surface does
      not do.
    -->
    <lw-toast-region />
    <terms-gate :user="user" />
    <!-- The guided tour (spec 25). Renders nothing unless one is running, and a
         story tour only ever starts on the public demo. -->
    <tour-host />
    <!-- Spec 53: the end of a practice run (posting it, the celebration). In a
         practice fleet only; nothing anywhere else. -->
    <training-run-host v-if="trainingLease" />
    <!-- Spec 53 15: a practice run as the task and its checklist, the default
         ("Do it your way"). In a practice fleet only, like the run host. -->
    <task-host v-if="trainingLease" />
  </div>
</template>
<script>
import tourHost from './components/TourHost.vue';
import trainingRunHost from './components/TrainingRunHost.vue';
import taskHost from './components/TaskHost.vue';
import headerComponent from './components/header/header.vue';
import LwNotice from './components/lw/LwNotice.vue';
import lwToastRegion from './components/lw/LwToastRegion.vue';
import sidebar from './components/sidebar/sidebar.vue';
import termsGate from './components/TermsGate.vue';
import api from '../config/api.js';
import moment from 'moment-timezone';
import { orgZone } from './lib/orgTime.js';

// This import is load-bearing beyond this file: 'moment-timezone' patches the
// one shared 'moment' module, which is why LoadList.vue and LoadsView.vue can
// import plain 'moment' and still call .tz() (their suites shim it by hand,
// because their module graphs never reach this file).

// One number, shared with the stylesheet's phone block (app.scss) and the
// tour's docked card (tours/geometry.js NARROW_PX is 720 for the CARD; the
// shell turns sooner because a 208px sidebar already crowds a 768px tablet).
const NARROW_QUERY = '(max-width: 768px)';
function isNarrowViewport() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(NARROW_QUERY).matches
  );
}

export default {
  components: {
    headerComponent,
    LwNotice,
    lwToastRegion,
    sidebar,
    termsGate,
    tourHost,
    trainingRunHost,
    taskHost,
  },
  props: {
    user: {
      required: true,
      type: Object,
    },
  },
  data() {
    // On a phone the menu starts CLOSED: open, it would cover the page the
    // person came to see (a prospect opening the demo from an email, first of
    // all). On a desktop it starts open, as it always has.
    const narrow = isNarrowViewport();
    return {
      narrow,
      menuCollapsed: narrow,
    };
  },
  computed: {
    // Defensive like Devices.vue:352-355 ($store && $store.getters && ...):
    // the app.test.js idiom mocks $store as a bare object, not a real Vuex
    // store instance.
    showBanner() {
      return !!(this.$store && this.$store.getters && this.$store.getters.showBillingBanner);
    },
    bannerText() {
      return (this.$store && this.$store.getters && this.$store.getters.billingBannerText) || '';
    },
    billingStatus() {
      return (this.$store && this.$store.getters && this.$store.getters.billingStatus) || null;
    },
    trainingLease() {
      return Boolean(this.$store && this.$store.getters && this.$store.getters.trainingLease);
    },
  },
  watch: {
    // A phone's menu is a layer over the page, so choosing a destination has
    // to close it; a desktop's is a column beside the page and stays put.
    '$route.path'() {
      if (this.narrow) this.menuCollapsed = true;
    },
    // THE APP-WIDE DISPLAY ZONE. Every `moment()` in the SPA that does not name
    // a zone of its own formats in whatever moment's default is, so this is the
    // one write of it.
    //
    // SEMANTIC CHANGE, stated rather than smuggled. This used to be a
    // module-level `moment.tz.setDefault('PST')`, and 'PST' is not an IANA zone
    // name: moment-timezone has no data for it, so setDefault logged "Moment
    // Timezone has no data for PST" and set the default to NULL. The intent was
    // Pacific time; the effect was the browser's own zone plus a console error.
    // Dates therefore rendered in the viewer's zone, which on a dispatcher's
    // machine in Denver is not the zone the office's paperwork uses.
    //
    // It is now the tenant's `org.timezone` (default America/Los_Angeles, which
    // is what the intent was), so the whole app agrees with the print sheets
    // and with each other. On a machine already in the org's zone nothing
    // visibly changes; on any other machine times move to the org's zone, which
    // is the point.
    //
    // WATCHED, not read once in created(): main.js dispatches
    // fetchOrganization without awaiting it and mounts the app immediately, so
    // the organization arrives after this component exists. `immediate: true`
    // sets the fallback zone for the interval before it lands (and for a tenant
    // whose fetch failed, which main.js swallows), and the watcher sets the
    // real one when it does.
    //
    // The path is resolved defensively by Vue itself — a `$store` mocked as a
    // bare object, which this file's suite does, yields undefined rather than
    // throwing — and orgZone() answers the fallback for it.
    '$store.state.organization': {
      immediate: true,
      handler() {
        moment.tz.setDefault(orgZone(this.$store));
      },
    },
  },
  mounted() {
    // Turning a phone on its side, or dragging a desktop window narrow: follow
    // it. Crossing INTO narrow closes the menu, crossing out restores the column.
    if (typeof window.matchMedia !== 'function') return;
    this.narrowQuery = window.matchMedia(NARROW_QUERY);
    this.onNarrowChange = event => {
      this.narrow = event.matches;
      this.menuCollapsed = event.matches;
    };
    if (this.narrowQuery.addEventListener) {
      this.narrowQuery.addEventListener('change', this.onNarrowChange);
    }
  },
  beforeUnmount() {
    if (this.narrowQuery && this.narrowQuery.removeEventListener) {
      this.narrowQuery.removeEventListener('change', this.onNarrowChange);
    }
  },
  beforeCreate() {
    // Any time an error where no authentication credentials are present, attempt to get fresh credentials.
    this.$http.interceptors.response.use(undefined, err => {
      // Error 401 is returned from the API whenever the client needs to
      // reauthenticate, so this redirects to the login page so the browser
      // attempts to navigate to the API directly (via the auth route) and
      // then shows the user the appropriate error message (network
      // unavailable, SSL issue, etc.). This branch is unchanged.
      if (err.response && err.response.status === 401) {
        window.location = api.login;
      } else if (!err.response) {
        // SEMANTIC CHANGE, not a design change (refresh_plan.md, directly
        // under the phase 2 surface table: "the hard redirect to login on
        // any network failure with only a console.log" is one of "two items
        // in phase 2 [that] are engineering, not design, and should be filed
        // separately but not deferred past it"). audit_web.md 3.8: this
        // branch used to redirect to login on !err.response with only
        // console.log('auth error') — "a momentary connectivity blip throws
        // the dispatcher out of the app with no message." A request that
        // never reached the server is not evidence the session is invalid,
        // so it must no longer bounce the dispatcher to login. It now
        // dispatches a pinned danger toast instead (toasts.js: a danger
        // toast does not auto-dismiss, which is correct here — "the office
        // did not get it" is exactly the class of message this product must
        // never let a dispatcher miss) and still rethrows below, so every
        // existing .catch on the calling page keeps running unchanged.
        //
        // The one risk this trades in: an auth failure that happens to
        // surface as a network error rather than a clean 401 (a
        // CORS-blocked redirect, say) now leaves the dispatcher inside the
        // app with a toast instead of being bounced to login. A reload
        // recovers it, because the next request either succeeds or answers
        // a real 401.
        //
        // Defensive in the same idiom as the showBanner computed above:
        // this suite mocks $store as a bare object ({ getters }), so a
        // network-failure test with no dispatch key must not throw here.
        if (this.$store && this.$store.dispatch) {
          this.$store.dispatch('toasts/notify', {
            tone: 'danger',
            message:
              "Ladeway couldn't reach the server. Check your connection — your last action may not have saved.",
          });
        }
      }

      // spec 12 Part 4: a suspended tenant's every operations call answers
      // 402, so this is a redirect to the restricted Subscription page
      // rather than a message. The name check against currentRoute stops
      // vue-router's NavigationDuplicated error loop when already there
      // (every subsequent call on that page would otherwise re-trigger it).
      if (err.response && err.response.status === 402) {
        // currentRoute is a ref under vue-router 4, hence `.value`.
        if (this.$router && this.$router.currentRoute.value.name !== 'Subscription') {
          this.$router.replace({ name: 'Subscription' });
        }
      }

      // Spec 53 3.2: the server says this person's account is shut until their
      // training is done, which the shell should never have been mounted for
      // (root.js renders the bare layout for a gated user). A reload asks
      // /users/me again and lands them on the training gate. Not on the gate
      // page itself, so a disagreement can never become a reload loop.
      const data = err.response && err.response.data;
      if (
        err.response &&
        err.response.status === 403 &&
        data &&
        data.error_code === 'TRAINING_REQUIRED' &&
        window.location.pathname !== '/training'
      ) {
        window.location.assign('/training');
      }

      // Error should always been thrown to allow downstream handling
      throw err;
    });
  },
};
</script>

<!--
  The `@media print { .dispatch-print ... }` block that used to live here has
  moved to ui/src/sass/_print.scss, imported from app.scss. It was 110 lines
  of a separate visual system (print sheets are surface 10, refresh_plan.md
  section 4's migration table) sharing this file with the shell for no reason
  other than history; app.scss now carries a comment of its own explaining
  where it lives and why. This file is left with exactly one non-scoped
  <style> block below, which is the one MIGRATED_SURFACES exemption app.vue
  is granted: the global stylesheet import, which cannot itself be scoped.
-->

<style lang="scss">
@import '../sass/app.scss';
</style>

<style scoped lang="scss">
// The banner is a flex CHILD of #main, and the div it replaced carried
// `flex: none`. A component's root element is the one thing a parent's scoped
// CSS may style, and layout — where a thing sits in its parent — is the one
// thing a parent should be styling: without this the notice is `0 1 auto` and
// can be squeezed by a tall page, which is the failure the original
// declaration existed to prevent. Everything INSIDE the notice is still
// LwNotice's own business.
.lw-p-billing-banner {
  flex: none;
}

// The one other class this file declares, in the lw-p- page namespace
// tokenPurity.test.js's migrated-surface rules require. LwNotice's actions
// slot lays its children out in a flex row but styles nothing inside it, so
// the link needs its own rule to read as part of the notice rather than as a
// default blue browser link.
.lw-p-banner-link {
  color: inherit;
  font: var(--lw-type-caption-strong);
  text-decoration: underline;
}
</style>
